import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Invitation } from '@/api/invitations/invitationsApi.types';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import {
  useResendWorkspaceInvitationMutation,
  useRevokeWorkspaceInvitationMutation,
  useWorkspaceInvitationsQuery,
} from '@/hooks/useWorkspaceTeam';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { INVITATION_DISPLAY_STATUS, resolveInvitationStatus } from './resolveInvitationStatus';

export function InvitationsPanel() {
  const { t } = useTranslation('team');
  const [page, setPage] = useState(1);
  const invitationsQuery = useWorkspaceInvitationsQuery(page);
  const resendMutation = useResendWorkspaceInvitationMutation();
  const revokeMutation = useRevokeWorkspaceInvitationMutation();

  const result = invitationsQuery.data;
  const invitations = result === undefined ? [] : result.items;
  const totalItems = result === undefined ? 0 : result.totalItems;

  async function resend(invitationId: string) {
    try {
      await resendMutation.mutateAsync(invitationId);
      showSuccessNotification(t('invitations.resent'));
    } catch (resendError) {
      const backendMessage = resolveApiErrorMessage(resendError);
      showErrorNotification(
        backendMessage.length > 0 ? backendMessage : t('invitations.resendError')
      );
    }
  }

  async function revoke(invitationId: string) {
    try {
      await revokeMutation.mutateAsync(invitationId);
      showSuccessNotification(t('invitations.revoked'));
    } catch (revokeError) {
      const backendMessage = resolveApiErrorMessage(revokeError);
      showErrorNotification(
        backendMessage.length > 0 ? backendMessage : t('invitations.revokeError')
      );
    }
  }

  const isBusy = resendMutation.isPending || revokeMutation.isPending;

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Mail" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('invitations.heading')}</h2>
      </div>
      {invitationsQuery.isLoading === true ? (
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : invitationsQuery.isError === true || result === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            invitationsQuery.refetch();
          }}
        />
      ) : invitations.length === 0 ? (
        <p className="text-[13px] text-zoomuted py-1">{t('invitations.empty')}</p>
      ) : (
        <>
          <div className="divide-y divide-zooborder">
            {invitations.map(function renderInvitation(invitation) {
              return (
                <InvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  isBusy={isBusy}
                  isResending={
                    resendMutation.isPending && resendMutation.variables === invitation.id
                  }
                  isRevoking={
                    revokeMutation.isPending && revokeMutation.variables === invitation.id
                  }
                  onResend={function requestResend() {
                    void resend(invitation.id);
                  }}
                  onRevoke={function requestRevoke() {
                    void revoke(invitation.id);
                  }}
                />
              );
            })}
          </div>
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </Card>
  );
}

interface InvitationRowProps {
  invitation: Invitation;
  isBusy: boolean;
  isResending: boolean;
  isRevoking: boolean;
  onResend: () => void;
  onRevoke: () => void;
}

function InvitationRow({
  invitation,
  isBusy,
  isResending,
  isRevoking,
  onResend,
  onRevoke,
}: InvitationRowProps) {
  const { t } = useTranslation('team');
  const status = resolveInvitationStatus(invitation.expiresAt, new Date());
  const isExpired = status === INVITATION_DISPLAY_STATUS.expired;

  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-zooindigo truncate">{invitation.email}</div>
        <Badge tone={isExpired ? 'warning' : 'neutral'} size="sm" className="mt-1">
          {isExpired ? t('invitations.status.expired') : t('invitations.status.pending')}
        </Badge>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="small"
          disabled={isBusy}
          isLoading={isResending}
          onClick={onResend}
        >
          {t('invitations.resend')}
        </Button>
        <Button
          variant="destructiveGhost"
          size="icon"
          aria-label={t('invitations.revoke')}
          title={t('invitations.revoke')}
          disabled={isBusy}
          isLoading={isRevoking}
          onClick={onRevoke}
        >
          {isRevoking ? null : <Icon name="X" />}
        </Button>
      </div>
    </div>
  );
}
